import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { PageHeader } from "@/pages/layoutChrome";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import api from "@/lib/api";
import { usePersistedClientId } from "@/lib/clientSelection";
import { TRAMITES_CONTEXT_LABEL } from "@/modules/tramites/manifest";
import { MATERIALIDAD_CONTEXT_LABEL } from "@/modules/materialidad/manifest";
import MaterialidadConfigPanel from "@/modules/tramites/materialidad/MaterialidadConfigPanel";
import MaterialidadCriteriosRiesgoPanel from "@/modules/tramites/materialidad/MaterialidadCriteriosRiesgoPanel";
import MaterialidadEfosPanel from "@/modules/tramites/materialidad/MaterialidadEfosPanel";
import MaterialidadFaltantesPanel from "@/modules/tramites/materialidad/MaterialidadFaltantesPanel";
import MaterialidadPortalPanel from "@/modules/tramites/materialidad/MaterialidadPortalPanel";
import MaterialidadResumenPanel from "@/modules/tramites/materialidad/MaterialidadResumenPanel";
import {
  MATERIALIDAD_TAB_CONFIG,
  MATERIALIDAD_TAB_CRITERIOS,
  MATERIALIDAD_TAB_EFOS,
  MATERIALIDAD_TAB_EFOS_LABEL,
  MATERIALIDAD_TAB_FALTANTES,
  MATERIALIDAD_TAB_FALTANTES_LABEL,
  MATERIALIDAD_TAB_PORTAL,
  MATERIALIDAD_TAB_PORTAL_LABEL,
  MATERIALIDAD_TAB_RESUMEN,
} from "@/modules/tramites/materialidad/materialidadConstants";

/**
 * Materialidad — vista operativa (faltantes + resumen + criterios + config).
 * Ruta canónica: /materialidad/expediente.
 * Cliente de trabajo: barra superior del Layout (sin duplicar ModuleClientSelector aquí).
 */
export default function TramitesMaterialidadPage() {
  const location = useLocation();
  const [clients, setClients] = useState([]);
  const [clientId] = usePersistedClientId(clients, { autoPickFirst: false });
  const [activeTab, setActiveTab] = useState(MATERIALIDAD_TAB_FALTANTES);
  const isMaterialidadModule = String(location.pathname || "").startsWith("/materialidad");

  useEffect(() => {
    api.get("/clients").then(({ data }) => setClients(Array.isArray(data) ? data : [])).catch(() => setClients([]));
  }, []);

  const contextLabel = isMaterialidadModule ? MATERIALIDAD_CONTEXT_LABEL : TRAMITES_CONTEXT_LABEL;
  const backPath = isMaterialidadModule ? "/" : "/tramites/inicio";
  const backLabel = isMaterialidadModule ? "Volver al Panel General" : `Volver a ${TRAMITES_CONTEXT_LABEL}`;
  const backTestId = isMaterialidadModule ? "back-to-panel-general" : "back-to-tramites-hub";

  return (
    <div className="min-h-full" data-testid="tramites-materialidad-page">
      <PageHeader
        title={MATERIALIDAD_CONTEXT_LABEL}
        subtitle="Revisión del despacho, portal de clientes, cruce EFOS/69-B y riesgos por periodo."
        breadcrumb={[contextLabel]}
      />

      <div className="px-8 pt-2 max-w-6xl">
        <Link
          to={backPath}
          className="inline-flex items-center gap-2 rounded-sm border border-[#0F2D5C]/25 bg-[#0F2D5C]/[0.04] px-3 py-1.5 text-xs font-semibold text-[#0F2D5C] shadow-sm hover:bg-[#0F2D5C]/10 mb-4"
          data-testid={backTestId}
        >
          <ArrowLeft className="w-4 h-4 shrink-0" strokeWidth={2.25} />
          {backLabel}
        </Link>
      </div>

      <div className="px-8 pb-8 max-w-6xl space-y-4">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="rounded-sm flex flex-wrap h-auto gap-1">
            <TabsTrigger value={MATERIALIDAD_TAB_FALTANTES} className="rounded-sm" data-testid="materialidad-tab-faltantes">
              {MATERIALIDAD_TAB_FALTANTES_LABEL}
            </TabsTrigger>
            <TabsTrigger value={MATERIALIDAD_TAB_PORTAL} className="rounded-sm" data-testid="materialidad-tab-portal">
              {MATERIALIDAD_TAB_PORTAL_LABEL}
            </TabsTrigger>
            <TabsTrigger value={MATERIALIDAD_TAB_RESUMEN} className="rounded-sm" data-testid="materialidad-tab-resumen">
              Resumen
            </TabsTrigger>
            <TabsTrigger value={MATERIALIDAD_TAB_EFOS} className="rounded-sm" data-testid="materialidad-tab-efos">
              {MATERIALIDAD_TAB_EFOS_LABEL}
            </TabsTrigger>
            <TabsTrigger value={MATERIALIDAD_TAB_CRITERIOS} className="rounded-sm" data-testid="materialidad-tab-criterios">
              Criterios de riesgo
            </TabsTrigger>
            <TabsTrigger value={MATERIALIDAD_TAB_CONFIG} className="rounded-sm" data-testid="materialidad-tab-config">
              Configuración
            </TabsTrigger>
          </TabsList>

          <TabsContent value={MATERIALIDAD_TAB_FALTANTES}>
            {activeTab === MATERIALIDAD_TAB_FALTANTES ? (
              <MaterialidadFaltantesPanel
                key="materialidad-faltantes"
                clientId={clientId}
                clients={clients}
              />
            ) : null}
          </TabsContent>

          <TabsContent value={MATERIALIDAD_TAB_PORTAL}>
            {activeTab === MATERIALIDAD_TAB_PORTAL ? (
              <MaterialidadPortalPanel clientId={clientId} clients={clients} />
            ) : null}
          </TabsContent>

          <TabsContent value={MATERIALIDAD_TAB_RESUMEN}>
            <MaterialidadResumenPanel clientId={clientId} />
          </TabsContent>

          <TabsContent value={MATERIALIDAD_TAB_EFOS}>
            {activeTab === MATERIALIDAD_TAB_EFOS ? (
              <MaterialidadEfosPanel clientId={clientId} clients={clients} />
            ) : null}
          </TabsContent>

          <TabsContent value={MATERIALIDAD_TAB_CRITERIOS}>
            <MaterialidadCriteriosRiesgoPanel />
          </TabsContent>

          <TabsContent value={MATERIALIDAD_TAB_CONFIG}>
            <MaterialidadConfigPanel clientId={clientId} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
